export interface ParsedPairing {
  sid: string;
  pin: string;
  agentPub: Uint8Array;
}

export type ParseResult =
  | { ok: true; data: ParsedPairing }
  | { ok: false; reason: 'no_hash' | 'malformed_hash' };

const PIN_RE = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{8}$/;
// SPEC §1.2: session_id is exactly 16 chars from RFC 4648 base32 lowercase
// alphabet (a-z, 2-7). Anything else is locally malformed — no need to
// round-trip through the relay just to get a 4404.
const SID_RE = /^[a-z2-7]{16}$/;

export function parseHash(rawHash: string): ParseResult {
  const s = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
  if (!s) return { ok: false, reason: 'no_hash' };

  // Split on the first two '-' separators only.
  // SID and PIN never contain '-'; the pubkey (base64url) may, so we
  // must not split from the right.
  const firstDash = s.indexOf('-');
  if (firstDash < 0) return { ok: false, reason: 'malformed_hash' };
  const sid = s.slice(0, firstDash);

  const secondDash = s.indexOf('-', firstDash + 1);
  if (secondDash < 0) return { ok: false, reason: 'malformed_hash' };
  const pin = s.slice(firstDash + 1, secondDash);
  const agentB64 = s.slice(secondDash + 1);

  if (pin.length !== 8 || !PIN_RE.test(pin)) {
    return { ok: false, reason: 'malformed_hash' };
  }
  if (!SID_RE.test(sid)) {
    return { ok: false, reason: 'malformed_hash' };
  }

  const agentPub = decodeBase64UrlNoPad(agentB64);
  if (!agentPub || agentPub.byteLength !== 32) {
    return { ok: false, reason: 'malformed_hash' };
  }

  return { ok: true, data: { sid, pin, agentPub } };
}

function decodeBase64UrlNoPad(s: string): Uint8Array | null {
  if (!/^[A-Za-z0-9\-_]*$/.test(s)) return null;
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
  try {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}
